/* ------------------------------------------------------------------ */
/* Skeleton primitives                                                 */
/* ------------------------------------------------------------------ */

function SkeletonBox({ className }: { className?: string }) {
	return <div className={`animate-pulse rounded bg-app-surface-light ${className ?? ""}`} />
}

function SkeletonCircle({ size = "md" }: { size?: "xs" | "sm" | "md" | "xl" }) {
	const sizes = { xs: "h-7 w-7", sm: "h-9 w-9", md: "h-10 w-10", xl: "h-20 w-20" }
	return <div className={`${sizes[size]} animate-pulse rounded-full bg-app-surface-light`} />
}

/* ------------------------------------------------------------------ */
/* Composed skeletons                                                  */
/* ------------------------------------------------------------------ */

function PostCardSkeleton() {
	return (
		<div className="border-app-border border-b px-3 py-3 sm:px-4">
			<div className="flex gap-3">
				<SkeletonCircle />
				<div className="min-w-0 flex-1 space-y-2.5">
					<div className="flex items-center gap-2">
						<SkeletonBox className="h-3.5 w-28" />
						<SkeletonBox className="h-3 w-20" />
					</div>
					<SkeletonBox className="h-3.5 w-full" />
					<SkeletonBox className="h-3.5 w-3/4" />
					<div className="flex gap-6 pt-1">
						<SkeletonBox className="h-4 w-10" />
						<SkeletonBox className="h-4 w-10" />
						<SkeletonBox className="h-4 w-10" />
						<SkeletonBox className="h-4 w-10" />
					</div>
				</div>
			</div>
		</div>
	)
}

function NotificationItemSkeleton() {
	return (
		<div className="flex items-start gap-3 border-app-border border-b px-3 py-3 sm:px-4">
			<SkeletonCircle size="sm" />
			<div className="min-w-0 flex-1 space-y-1.5">
				<SkeletonBox className="h-3.5 w-48" />
				<SkeletonBox className="h-3 w-16" />
			</div>
		</div>
	)
}

function UserListItemSkeleton() {
	return (
		<div className="flex items-center gap-3 border-app-border border-b px-3 py-3 sm:px-4">
			<SkeletonCircle />
			<div className="min-w-0 flex-1 space-y-1.5">
				<SkeletonBox className="h-3.5 w-28" />
				<SkeletonBox className="h-3 w-20" />
			</div>
			<SkeletonBox className="h-7 w-16 rounded-full" />
		</div>
	)
}

function UserProfileHeaderSkeleton() {
	return (
		<div className="border-app-border border-b px-3 py-3 sm:px-4 sm:py-4">
			<div className="flex items-start justify-between">
				<SkeletonCircle size="xl" />
				<SkeletonBox className="h-8 w-24 rounded-full" />
			</div>
			<div className="mt-3 space-y-2">
				<SkeletonBox className="h-5 w-40" />
				<SkeletonBox className="h-3.5 w-24" />
			</div>
			<div className="mt-2 space-y-1.5">
				<SkeletonBox className="h-3.5 w-full" />
				<SkeletonBox className="h-3.5 w-2/3" />
			</div>
			<div className="mt-3 flex gap-4">
				<SkeletonBox className="h-4 w-16" />
				<SkeletonBox className="h-4 w-20" />
				<SkeletonBox className="h-4 w-20" />
			</div>
		</div>
	)
}

function SidebarUserSkeleton() {
	return (
		<div className="flex items-center gap-3 py-1.5">
			<SkeletonCircle size="sm" />
			<div className="min-w-0 flex-1 space-y-1">
				<SkeletonBox className="h-3 w-24" />
				<SkeletonBox className="h-2.5 w-16" />
			</div>
			<SkeletonBox className="h-6 w-14 rounded-full" />
		</div>
	)
}

function SidebarTrendingSkeleton() {
	return (
		<div className="space-y-1 py-1.5">
			<div className="flex items-center gap-1.5">
				<SkeletonCircle size="xs" />
				<SkeletonBox className="h-3 w-20" />
			</div>
			<SkeletonBox className="h-3 w-full" />
			<SkeletonBox className="h-2.5 w-16" />
		</div>
	)
}

export function SidebarSkeleton() {
	return (
		<div className="space-y-4 pt-2 pb-6">
			<div className="rounded-xl bg-app-surface p-4">
				<SkeletonBox className="mb-3 h-5 w-28" />
				<div className="space-y-3">
					<SidebarUserSkeleton />
					<SidebarUserSkeleton />
					<SidebarUserSkeleton />
					<SidebarUserSkeleton />
				</div>
			</div>
			<div className="rounded-xl bg-app-surface p-4">
				<SkeletonBox className="mb-3 h-5 w-20" />
				<div className="space-y-3">
					<SidebarTrendingSkeleton />
					<SidebarTrendingSkeleton />
					<SidebarTrendingSkeleton />
					<SidebarTrendingSkeleton />
					<SidebarTrendingSkeleton />
				</div>
			</div>
		</div>
	)
}

/* ------------------------------------------------------------------ */
/* Page-level skeleton compositions                                    */
/* ------------------------------------------------------------------ */

function FeedTabsStatic() {
	return (
		<div className="sticky top-0 z-10 flex border-app-border border-b bg-app-bg/80 backdrop-blur-md">
			<div className="flex-1 border-neon border-b-2 py-3 text-center font-semibold text-app-text text-sm">
				Trending
			</div>
			<div className="flex-1 py-3 text-center font-semibold text-app-text-secondary text-sm">
				Latest
			</div>
		</div>
	)
}

export function FeedListSkeleton() {
	return (
		<div>
			<PostCardSkeleton />
			<PostCardSkeleton />
			<PostCardSkeleton />
			<PostCardSkeleton />
			<PostCardSkeleton />
		</div>
	)
}

export function FeedPageSkeleton() {
	return (
		<div>
			<FeedTabsStatic />
			<FeedListSkeleton />
		</div>
	)
}

export function NotificationsPageSkeleton() {
	return (
		<div>
			<div className="flex items-center justify-between border-app-border border-b px-3 py-3 sm:px-4">
				<SkeletonBox className="h-4 w-24" />
			</div>
			<NotificationItemSkeleton />
			<NotificationItemSkeleton />
			<NotificationItemSkeleton />
			<NotificationItemSkeleton />
			<NotificationItemSkeleton />
			<NotificationItemSkeleton />
			<NotificationItemSkeleton />
			<NotificationItemSkeleton />
		</div>
	)
}

export function UserPageSkeleton() {
	return (
		<div>
			<UserProfileHeaderSkeleton />
			<PostCardSkeleton />
			<PostCardSkeleton />
			<PostCardSkeleton />
		</div>
	)
}

export function PostPageSkeleton() {
	return (
		<div>
			<PostCardSkeleton />
			<div className="border-app-border border-b px-3 py-3 sm:px-4">
				<SkeletonBox className="h-3.5 w-40" />
			</div>
			<div className="border-app-border border-b px-3 py-2 sm:px-4">
				<SkeletonBox className="h-4 w-16" />
			</div>
			<PostCardSkeleton />
			<PostCardSkeleton />
			<PostCardSkeleton />
		</div>
	)
}

export function SearchDiscoverSkeleton() {
	return (
		<div>
			<div className="px-4 pt-4 pb-2">
				<SkeletonBox className="h-5 w-24" />
			</div>
			<UserListItemSkeleton />
			<UserListItemSkeleton />
			<UserListItemSkeleton />
			<UserListItemSkeleton />
			<div className="px-4 pt-4 pb-2">
				<SkeletonBox className="h-5 w-32" />
			</div>
			<PostCardSkeleton />
			<PostCardSkeleton />
			<PostCardSkeleton />
			<PostCardSkeleton />
		</div>
	)
}

export function SearchResultsSkeleton({ tab }: { tab: "people" | "posts" }) {
	if (tab === "people") {
		return (
			<div>
				<UserListItemSkeleton />
				<UserListItemSkeleton />
				<UserListItemSkeleton />
				<UserListItemSkeleton />
			</div>
		)
	}
	return (
		<div>
			<PostCardSkeleton />
			<PostCardSkeleton />
			<PostCardSkeleton />
			<PostCardSkeleton />
		</div>
	)
}

function TransactionItemSkeleton() {
	return (
		<div className="flex items-center justify-between border-app-border border-b px-3 py-3 sm:px-4">
			<div className="space-y-1.5">
				<SkeletonBox className="h-3.5 w-24" />
				<SkeletonBox className="h-3 w-16" />
			</div>
			<SkeletonBox className="h-3.5 w-16" />
		</div>
	)
}

export function WalletTransactionsSkeleton() {
	return (
		<div>
			<TransactionItemSkeleton />
			<TransactionItemSkeleton />
			<TransactionItemSkeleton />
			<TransactionItemSkeleton />
		</div>
	)
}

export function ReferralPageSkeleton() {
	return (
		<div>
			<div className="border-app-border border-b px-3 py-3 sm:px-4">
				<SkeletonBox className="h-5 w-20" />
			</div>
			<div className="p-4 sm:p-6">
				<SkeletonBox className="mb-3 h-5 w-40" />
				<SkeletonBox className="mb-2 h-14 w-full rounded-xl" />
				<SkeletonBox className="mb-6 h-3.5 w-3/4" />
				<SkeletonBox className="mb-3 h-5 w-12" />
				<SkeletonBox className="h-20 w-full rounded-xl" />
			</div>
		</div>
	)
}

export function FollowListSkeleton() {
	return (
		<div>
			<UserListItemSkeleton />
			<UserListItemSkeleton />
			<UserListItemSkeleton />
			<UserListItemSkeleton />
			<UserListItemSkeleton />
			<UserListItemSkeleton />
			<UserListItemSkeleton />
			<UserListItemSkeleton />
		</div>
	)
}
